import invariant from 'tiny-invariant';

export interface Config {
  // Application
  APPLICATION_NAME: string;
  NODE_ENV?: string;
  CI?: boolean;
  SENTRY_ENV?: string;
  LOG_LEVEL?: string;
  VERSION: string;

  // EHI Application specific
  TINYMCE_API_KEY?: string;
  HYPERION_API_SERVER?: string;
  HELLOSIGN_CLIENT_APP?: string;
  INTERCOM_APP_ID?: string;

  // APIs
  API_URI: string;
  API_URI_V2: string;
  GRAPHQL_URI: string;
  GRAPHQL_DIRECT_URI: string;
  PRODUCT_CATALOG_GRAPHQL_DIRECT_URI?: string;
  GRAPHQL_DISABLE_BATCHING: boolean;
  PARTNERS_API_URI: string;
  PRODUCTS_URI: string;
  INSPIRE_ME_API_URI: string;
  PRODUCT_CATALOG_URI: string;
  /**
   * This is currently only available in development environments, use
   * `getApiEnvironment` from `src/shared/environment` to get the current API
   * environment across deployments.
   *
   * @remarks This is currently a development-only concept that exists in
   * development `.env` files. We need to include it in our configuration,
   * because we're currently deriving the API environment provided from
   * `getApiEnvironment` from the `GRAPHQL_URI` environent variable which is
   * always the same in development due to the development reverse proxy. If we
   * were to ever make this a canonical value, we'd want to make this a required
   * environment variable and update the implementation of `getApiEnvironment`.
   */
  API_ENV?: string;

  // Telemetry
  BRANCH_API_KEY?: string;
  SEGMENT_API_KEY?: string;
  SEGMENT_EHI_API_KEY?: string;
  SENTRY_DESIGN_STUDIO_DSN?: string;
  SENTRY_EHI_DSN?: string;
  SENTRY_RENDERING_HUB_DSN?: string;
  SENTRY_HOMEOWNER_APP_DSN?: string;
  VIRTUAL_WALKTHROUGH_DSN?: string;
  SENTRY_DSN?: string;
  SPLIT_API_KEY?: string;
  NEW_GTM_API_KEY?: string;
  VWO_ACCOUNT_ID?: string;

  // Firebase
  EMULATOR_FIREBASE_AUTH?: string;
  EMULATOR_FIREBASE_FIRESTORE?: string;
  FIREBASE_API_KEY?: string;
  FIREBASE_APP_ID?: string;
  FIREBASE_AUTH_DOMAIN?: string;
  FIREBASE_DATABASE_URL?: string;
  FIREBASE_MEASUREMENT_ID?: string;
  FIREBASE_MESSAGING_SENDER_ID?: string;
  FIREBASE_PROJECT_ID?: string;
  FIREBASE_STORAGE_BUCKET?: string;

  // One Trust
  ONETRUST_SDK_ID?: string;

  // Design Studio
  GUEST_FILE_PATH?: string;

  // Homeowner Org Code
  HOMEOWNER_ORG_CODE?: string;
  DTC_PARTNER_ID?: string;

  // Google Maps
  GOOGLE_MAPS_API_KEY?: string;

  // Virtual Walkthrough
  VW_DEV_PROXY: boolean;
  VW_DEV_PROXY_API_URI?: string;
  VW_DEV_PROXY_API_URI_V2?: string;
}

const env = (
  typeof window !== 'undefined' ? window.env : process.env
) as Record<keyof Config | (string & {}), string>;

/**
 * There is a very specific reason why this function needs to exist, and why we can't just use !!env.VAR
 *
 * Given an ENV variable FOO, defined as:
 * FOO=true
 *
 * The local node dev server will treat the value as a string. ie ('true')
 * Howerver, the browsers javascript will treat the value as a boolean, ie (true).
 * The !! operation on a string will always return true if it is not an empty string. (ie !!'false' === true)
 */
function parseBooleanEnv(
  envValue: string | undefined | boolean,
): boolean | undefined {
  if (envValue === undefined) {
    return undefined;
  }

  if (typeof envValue === 'boolean') {
    return envValue;
  }

  if (envValue === 'true') {
    return true;
  }

  return false;
}

invariant(
  env.GRAPHQL_URI && typeof env.GRAPHQL_URI === 'string',
  '💣💥 ENV_CONFIG_ERROR: The "GRAPHQL_URI" value is not a valid string',
);

invariant(
  env.GRAPHQL_DIRECT_URI && typeof env.GRAPHQL_DIRECT_URI === 'string',
  '💣💥 ENV_CONFIG_ERROR: The "GRAPHQL_DIRECT_URI" value is not a valid string',
);

invariant(
  env.API_URI && typeof env.API_URI === 'string',
  '💣💥 ENV_CONFIG_ERROR: The "API_URI" value is not a valid string',
);

invariant(
  env.API_URI_V2 && typeof env.API_URI_V2 === 'string',
  '💣💥 ENV_CONFIG_ERROR: The "API_URI_V2" value is not a valid string',
);

invariant(
  env.PARTNERS_API_URI && typeof env.PARTNERS_API_URI === 'string',
  '💣💥 ENV_CONFIG_ERROR: The "PARTNERS_API_URI" value is not a valid string',
);

invariant(
  env.PRODUCTS_URI && typeof env.PRODUCTS_URI === 'string',
  '💣💥 ENV_CONFIG_ERROR: The "PRODUCTS_URI" value is not a valid string',
);

invariant(
  env.INSPIRE_ME_API_URI && typeof env.INSPIRE_ME_API_URI === 'string',
  '💣💥 ENV_CONFIG_ERROR: The "INSPIRE_ME_API_URI" value is not a valid string',
);

invariant(
  env.PRODUCT_CATALOG_URI && typeof env.PRODUCT_CATALOG_URI === 'string',
  '💣💥 ENV_CONFIG_ERROR: The "PRODUCT_CATALOG_URI" value is not a valid string',
);

if (parseBooleanEnv(env.VW_DEV_PROXY)) {
  invariant(
    env.VW_DEV_PROXY_API_URI && typeof env.VW_DEV_PROXY_API_URI === 'string',
    '💣💥 ENV_CONFIG_ERROR: The "VW_DEV_PROXY_API_URI" value is not a valid string',
  );
  invariant(
    env.VW_DEV_PROXY_API_URI_V2 &&
      typeof env.VW_DEV_PROXY_API_URI_V2 === 'string',
    '💣💥 ENV_CONFIG_ERROR: The "VW_DEV_PROXY_API_URI_V2" value is not a valid string',
  );
}

const config: Config = {
  // Application
  APPLICATION_NAME: 'web-react',
  LOG_LEVEL: env.LOG_LEVEL,
  NODE_ENV: env.NODE_ENV,
  CI: env.CI === 'true',
  SENTRY_ENV: env.SENTRY_ENV || env.NODE_ENV,
  // VERSION was renamed to APP_VERSION in the runtime environment.
  // To prevent renaming it all over the codebase, we're aliasing it here.
  VERSION: env.APP_VERSION ?? 'local',
  TINYMCE_API_KEY: env.TINYMCE_API_KEY,
  HYPERION_API_SERVER: env.HYPERION_API_SERVER,
  HELLOSIGN_CLIENT_APP: env.HELLOSIGN_CLIENT_APP,
  INTERCOM_APP_ID: env.INTERCOM_APP_ID,

  // APIs
  API_URI: env.API_URI,
  API_URI_V2: env.API_URI_V2,
  GRAPHQL_URI: env.GRAPHQL_URI,
  GRAPHQL_DIRECT_URI: env.GRAPHQL_DIRECT_URI,
  PRODUCT_CATALOG_GRAPHQL_DIRECT_URI: env.PRODUCT_CATALOG_GRAPHQL_DIRECT_URI,
  GRAPHQL_DISABLE_BATCHING:
    parseBooleanEnv(env.GRAPHQL_DISABLE_BATCHING) || false,
  PARTNERS_API_URI: env.PARTNERS_API_URI,
  PRODUCTS_URI: env.PRODUCTS_URI,
  INSPIRE_ME_API_URI: env.INSPIRE_ME_API_URI,
  PRODUCT_CATALOG_URI: env.PRODUCT_CATALOG_URI,
  // NOTE: this is currently development-only. See the
  // comment in the `Config` type declaration above.
  API_ENV:
    typeof window === 'undefined'
      ? // eslint-disable-next-line no-underscore-dangle
        process.env.API_ENV ?? process.env._API_ENV
      : env.API_ENV,

  // Telemetry
  BRANCH_API_KEY: env.BRANCH_API_KEY,
  SEGMENT_API_KEY: env.SEGMENT_API_KEY,
  SEGMENT_EHI_API_KEY: env.SEGMENT_EHI_API_KEY,
  SENTRY_DESIGN_STUDIO_DSN: env.SENTRY_DESIGN_STUDIO_DSN,
  SENTRY_EHI_DSN: env.SENTRY_EHI_DSN,
  SENTRY_RENDERING_HUB_DSN: env.SENTRY_RENDERING_HUB_DSN,
  SENTRY_HOMEOWNER_APP_DSN: env.SENTRY_HOMEOWNER_APP_DSN,
  VIRTUAL_WALKTHROUGH_DSN: env.VIRTUAL_WALKTHROUGH_DSN,
  SENTRY_DSN: env.SENTRY_DSN,
  SPLIT_API_KEY: env.SPLIT_API_KEY,
  NEW_GTM_API_KEY: env.NEW_GTM_API_KEY,
  VWO_ACCOUNT_ID: env.VWO_ACCOUNT_ID,

  // Firebase
  EMULATOR_FIREBASE_AUTH: env.EMULATOR_FIREBASE_AUTH,
  EMULATOR_FIREBASE_FIRESTORE: env.EMULATOR_FIREBASE_FIRESTORE,
  FIREBASE_API_KEY: env.FIREBASE_API_KEY,
  FIREBASE_APP_ID: env.FIREBASE_APP_ID,
  FIREBASE_AUTH_DOMAIN: env.FIREBASE_AUTH_DOMAIN,
  FIREBASE_DATABASE_URL: env.FIREBASE_DATABASE_URL,
  FIREBASE_MEASUREMENT_ID: env.FIREBASE_MEASUREMENT_ID,
  FIREBASE_MESSAGING_SENDER_ID: env.FIREBASE_MESSAGING_SENDER_ID,
  FIREBASE_PROJECT_ID: env.FIREBASE_PROJECT_ID,
  FIREBASE_STORAGE_BUCKET: env.FIREBASE_STORAGE_BUCKET,
  ONETRUST_SDK_ID: env.ONETRUST_SDK_ID,

  // Design Studio
  GUEST_FILE_PATH: env.GUEST_FILE_PATH,

  // Homeowner Org Code
  HOMEOWNER_ORG_CODE: env.HOMEOWNER_ORG_CODE,
  DTC_PARTNER_ID: env.DTC_PARTNER_ID,

  // Google Maps
  GOOGLE_MAPS_API_KEY: env.GOOGLE_MAPS_API_KEY,

  // Virtual Walkthrough
  VW_DEV_PROXY: parseBooleanEnv(env.VW_DEV_PROXY) || false,
  VW_DEV_PROXY_API_URI: env.VW_DEV_PROXY_API_URI,
  VW_DEV_PROXY_API_URI_V2: env.VW_DEV_PROXY_API_URI_V2,
};

export { config };
